import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Checkbox,
  ConfigProvider,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import { usePermissions } from '../../hooks/usePermissions';
import outreachRulesApi from '../../api/outreach-rules-api';
import RuleDraft from '../../components/outreach/RuleDraft';
import RuleSummary from '../../components/outreach/RuleSummary';
import RuleHistory, { actorText } from '../../components/outreach/RuleHistory';
import { emptyRule } from '../../components/outreach/RuleEditor';
import styles from '../../components/outreach/outreach.module.scss';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Uključeno' },
  { value: 'test', label: 'Test' },
  { value: 'off', label: 'Isključeno' },
];

const MODE_TEXT = {
  off: {
    label: 'Slanje isključeno',
    color: 'default',
    help: 'Pravila se ne izvršavaju automatski.',
  },
  'dry-run': {
    label: 'Probni rad',
    color: 'gold',
    help: 'Svakog sata se računa ko bi dobio poruku, ništa se ne šalje.',
  },
  live: {
    label: 'Slanje uključeno',
    color: 'green',
    help: 'Poruke se šalju korisnicima po pravilima sa statusom „Uključeno“.',
  },
};

const CONFIRM_LIVE =
  'Korisnici će dobijati poruke po pravilima sa statusom „Uključeno“, u okviru opštih ograničenja.';

const fmtTime = (iso) => {
  const d = dayjs(iso);
  return d.isSame(dayjs(), 'day')
    ? d.format('HH:mm')
    : d.format('DD.MM. HH:mm');
};

const fmtNum = (n) => Number(n || 0).toLocaleString('sr-RS');

const lastRunText = (s) => {
  if (!s.lastRunAt) return 'Još nije bilo provere.';
  const when = `Poslednja: ${fmtTime(s.lastRunAt)}`;
  const r = s.lastResult;
  if (s.lastSkippedBecauseOff || s.lastMode === 'off' || !r) {
    return `${when} — slanje isključeno, ništa nije provereno`;
  }
  const outcome =
    s.lastMode === 'dry-run'
      ? `bi dobilo ${fmtNum(r.send)}`
      : `poslato ${fmtNum(r.sent)}`;
  return `${when} — provereno ${fmtNum(r.evaluated)}, ${outcome}`;
};

const SchedulerStatus = ({ scheduler }) => {
  if (!scheduler) return null;
  return (
    <div className={styles.dim}>
      <div>
        {/* running = a check is in progress right now, not "the job exists". */}
        {scheduler.running
          ? 'Provera je upravo u toku…'
          : `Sledeća provera: ${fmtTime(scheduler.nextRunAt)}`}
      </div>
      <div>{lastRunText(scheduler)}</div>
    </div>
  );
};

SchedulerStatus.propTypes = {
  scheduler: PropTypes.shape({
    running: PropTypes.bool,
    lastRunAt: PropTypes.string,
    lastMode: PropTypes.string,
    lastResult: PropTypes.object,
    lastSkippedBecauseOff: PropTypes.bool,
    nextRunAt: PropTypes.string,
  }),
};

const DAYS = [
  ['monday', 'pon'],
  ['tuesday', 'uto'],
  ['wednesday', 'sre'],
  ['thursday', 'čet'],
  ['friday', 'pet'],
  ['saturday', 'sub'],
  ['sunday', 'ned'],
];

const EXAMPLES = [
  'Javi se korisniku koji ništa nije kupio 7 dana posle registracije',
  'Pohvali VIP korisnike koji imaju niz od 10 dana zaredom',
  'Podseti one koji su prestali da čekiraju podsetnike pre 5 do 15 dana, najviše jednom mesečno',
];

// Brand green instead of antd blue, as on the other dashboard pages.
const THEME = {
  token: {
    colorPrimary: '#005844',
    colorPrimaryHover: '#004636',
    colorLink: '#005844',
    colorLinkHover: '#004636',
    controlOutline: 'rgba(0, 88, 68, 0.1)',
  },
  components: {
    // The chosen rule status must stand out from the other two.
    Segmented: { itemSelectedBg: '#005844', itemSelectedColor: '#ffffff' },
  },
};

const STATUS_HELP =
  'Uključeno = šalje se; Test = samo se računa ko bi dobio poruku; Isključeno = ne računa se.';

/** A generate response as the draft panel wants it. */
const toDraft = (res, prompt, catalog) => ({
  rule: res.draft || res.raw || emptyRule(catalog),
  check: {
    valid: !!res.draft,
    errors: res.errors,
    warnings: res.warnings,
    pseudocode: res.pseudocode,
    preview: res.preview,
  },
  generation: {
    generationId: res.generationId,
    prompt,
    interpretation: res.interpretation,
    assumptions: res.assumptions,
    questions: res.questions,
    feasible: res.feasible,
    missing: res.missing,
  },
});

const RuleCard = ({ rule, catalog, canManage, onChanged, onHistory }) => {
  const [mode, setMode] = useState(null); // null | 'edit' | 'prompt'
  const [draft, setDraft] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const deleted = !!rule.deletedAt;

  const setStatus = async (status) => {
    const res = await outreachRulesApi.setStatus(rule.id, status);
    if (!res.success) toast.error(res.message);
    else {
      const label = STATUS_OPTIONS.find((o) => o.value === status)?.label;
      toast.success(`„${rule.definition.name}“: ${label}`);
      onChanged();
    }
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    const res = await outreachRulesApi.generate(prompt.trim(), rule.id);
    setBusy(false);
    if (!res.success) {
      toast.error(res.message);
      return;
    }
    setDraft(toDraft(res.data, prompt.trim(), catalog));
  };

  const openEditor = () => {
    setDraft({ rule: rule.definition, check: null });
    setMode('edit');
  };

  const act = async (fn, okText) => {
    const res = await fn();
    if (!res.success) toast.error(res.message);
    else {
      toast.success(okText);
      onChanged();
    }
  };

  return (
    <article className={`${styles.card} ${deleted ? styles.cardDeleted : ''}`}>
      <header className={styles.cardHead}>
        <div className={styles.cardTitle}>
          <h3>{rule.definition.name}</h3>
          <div className={styles.meta}>
            <span className={styles.dim}>
              v{rule.version} ·{' '}
              {dayjs(rule.updatedAt).format('DD.MM.YYYY. HH:mm')} ·{' '}
              {actorText(rule.updatedBy)}
            </span>
          </div>
        </div>
        {deleted ? (
          <Tag color="red">obrisano</Tag>
        ) : (
          <Tooltip title={STATUS_HELP}>
            <span>
              <Segmented
                size="small"
                options={STATUS_OPTIONS}
                value={rule.status}
                onChange={setStatus}
                disabled={!canManage}
              />
            </span>
          </Tooltip>
        )}
      </header>

      {!deleted && rule.blockedBy?.length > 0 && mode === null && (
        <div className={styles.blocked} role="alert">
          <strong>Ovo pravilo se trenutno ne šalje</strong>
          <ul>
            {rule.blockedBy.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          {canManage && (
            <div>
              <Button size="small" danger onClick={openEditor}>
                Popravi
              </Button>
            </div>
          )}
        </div>
      )}

      {mode === 'edit' && draft ? (
        <RuleDraft
          canReview={canManage}
          catalog={catalog}
          draft={draft}
          base={{ id: rule.id, version: rule.version }}
          onSaved={() => {
            setMode(null);
            setDraft(null);
            onChanged();
          }}
          onCancel={() => {
            setMode(null);
            setDraft(null);
          }}
        />
      ) : mode === 'prompt' ? (
        <div className={styles.promptEdit}>
          <RuleSummary
            catalog={catalog}
            rule={rule.definition}
            pseudocode={rule.pseudocode}
          />
          {!draft && (
            <div className={styles.promptRow}>
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="Šta da se promeni? npr. „Neka važi i za korisnike koji nisu čekirali 3 dana“"
                value={prompt}
                maxLength={1000}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <Space>
                <Button
                  type="primary"
                  onClick={generate}
                  loading={busy}
                  disabled={!prompt.trim()}
                >
                  Predloži izmenu
                </Button>
                <Button type="text" onClick={() => setMode(null)}>
                  Otkaži
                </Button>
              </Space>
              <span className={styles.dim}>
                AI dobija i dosadašnju istoriju ovog pravila.
              </span>
            </div>
          )}
          {draft && (
            <RuleDraft
              canReview={canManage}
              catalog={catalog}
              draft={draft}
              base={{ id: rule.id, version: rule.version }}
              onSaved={() => {
                setMode(null);
                setDraft(null);
                setPrompt('');
                onChanged();
              }}
              onCancel={() => {
                setDraft(null);
              }}
            />
          )}
        </div>
      ) : (
        <RuleSummary
          catalog={catalog}
          rule={rule.definition}
          pseudocode={rule.pseudocode}
        />
      )}

      {mode === null && (
        <div className={styles.cardActions}>
          {canManage && !deleted && (
            <>
              <Tooltip title="Ručna izmena polja">
                <Button size="small" onClick={openEditor}>
                  Izmeni
                </Button>
              </Tooltip>
              <Tooltip title="Opišite izmenu rečima, AI predlaže novu verziju">
                <Button size="small" onClick={() => setMode('prompt')}>
                  Izmeni opisom
                </Button>
              </Tooltip>
            </>
          )}
          <Tooltip title="Sve verzije i ko ih je napravio">
            <Button size="small" onClick={() => onHistory(rule)}>
              Istorija
            </Button>
          </Tooltip>
          {canManage && !deleted && (
            <Popconfirm
              title="Obrisati pravilo?"
              description="Pravilo prestaje da važi. Ostaje u istoriji i može se vratiti."
              okText="Obriši"
              cancelText="Ne"
              onConfirm={() =>
                act(() => outreachRulesApi.remove(rule.id), 'Pravilo obrisano')
              }
            >
              <Button size="small" danger>
                Obriši
              </Button>
            </Popconfirm>
          )}
          {canManage && deleted && (
            <Button
              size="small"
              onClick={() =>
                act(
                  () => outreachRulesApi.restore(rule.id),
                  'Pravilo vraćeno u test režim'
                )
              }
            >
              Vrati
            </Button>
          )}
        </div>
      )}
    </article>
  );
};

const Settings = ({ settings, canManage, onSaved }) => {
  const [form, setForm] = useState(settings.config);
  // Same key order as settings.config (form is a spread of it), except the weekday list.
  const normalized = (c) =>
    JSON.stringify({
      ...c,
      activeDays: DAYS.map(([d]) => d).filter((d) => c.activeDays?.includes(d)),
    });
  const changed = normalized(form) !== normalized(settings.config);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(settings.config), [settings]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const num = (key, label, min, max, help) => (
    <div className={styles.field}>
      <span>{label}</span>
      {help && <span className={styles.fieldHelp}>{help}</span>}
      <InputNumber
        className={styles.num}
        value={form[key]}
        min={min}
        max={max}
        onChange={(v) => set({ [key]: v })}
        disabled={!canManage}
      />
    </div>
  );
  const [confirmLive, setConfirmLive] = useState(false);
  const doSave = async () => {
    setSaving(true);
    const res = await outreachRulesApi.saveSettings(form);
    setSaving(false);
    setConfirmLive(false);
    if (!res.success) toast.error(res.message);
    else if (res.data?.unchanged) toast.info('Nema izmena za čuvanje.');
    else {
      toast.success('Podešavanja su sačuvana.');
      onSaved();
    }
  };
  const save = () => {
    if (form.mode === 'live' && settings.config.mode !== 'live') {
      setConfirmLive(true);
    } else doSave();
  };
  return (
    <div className={styles.settings}>
      {/* Same confirm pattern as BulkMarkPaidConfirmModal: btn-tertiary / btn-primary. */}
      <Modal
        open={confirmLive}
        title="Uključiti slanje?"
        onCancel={() => setConfirmLive(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setConfirmLive(false)}
            disabled={saving}
            className="btn-tertiary"
          >
            Otkaži
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={saving}
            onClick={doSave}
            className="btn-primary"
          >
            Da, uključi slanje
          </Button>,
        ]}
      >
        <p>{CONFIRM_LIVE}</p>
      </Modal>
      <p className={styles.dim}>
        Opšta ograničenja važe za sva pravila i nijedno pravilo ne može da ih
        zaobiđe.
      </p>
      <div className={styles.fields}>
        <div className={styles.field}>
          <span>Slanje</span>
          <span className={styles.fieldHelp}>
            Glavni prekidač za sve poruke koje AI šalje sam.
          </span>
          <Select
            value={form.mode}
            onChange={(v) => set({ mode: v })}
            options={Object.entries(MODE_TEXT).map(([value, m]) => ({
              value,
              label: m.label,
            }))}
            disabled={!canManage}
          />
        </div>
        <div className={styles.field}>
          <span>Tiho vreme od</span>
          <span className={styles.fieldHelp}>
            U ovom periodu se ne šalje ništa (vreme Beograda).
          </span>
          <Input
            value={form.quietStart}
            onChange={(e) => set({ quietStart: e.target.value })}
            disabled={!canManage}
          />
        </div>
        <div className={styles.field}>
          <span>do</span>
          <span className={styles.fieldHelp}>Kraj tihog perioda.</span>
          <Input
            value={form.quietEnd}
            onChange={(e) => set({ quietEnd: e.target.value })}
            disabled={!canManage}
          />
        </div>
        {num('maxPerDay', 'Najviše poruka dnevno', 0, 5, 'Po korisniku.')}
        {num('maxPerWeek', 'Najviše poruka u 7 dana', 0, 20, 'Po korisniku.')}
        {num(
          'minGapHours',
          'Najmanji razmak (sati)',
          0,
          720,
          'Između dve poruke istom korisniku.'
        )}
        {num(
          'skipIfChattedWithinHours',
          'Ne pisati ako je korisnik pisao u poslednjih (sati)',
          0,
          720,
          'Ako je korisnik sam pisao AI asistentu skoro, ne prekidamo ga.'
        )}
        {num(
          'unansweredLimit',
          'Neodgovorenih poruka zaredom',
          1,
          20,
          'Posle toliko poruka bez odgovora korisnika pravimo pauzu.'
        )}
        {num(
          'unansweredPauseDays',
          '…pa pauza (dana)',
          0,
          365,
          'Koliko dana se ne šalje ništa korisniku koji ne odgovara.'
        )}
        {num(
          'minAccountAgeDays',
          'Najmanja starost naloga (dana)',
          0,
          365,
          'Novi nalozi ne dobijaju poruke pre ovog roka.'
        )}
        {num(
          'maxChars',
          'Najduža poruka (znakova)',
          80,
          1000,
          'Duže poruke se ne šalju.'
        )}
        {num(
          'maxSendsPerSweep',
          'Najviše poruka po satu',
          0,
          5000,
          'Ukupno za sve korisnike u jednoj proveri.'
        )}
        <div className={styles.field}>
          <span>Push notifikacija</span>
          <span className={styles.fieldHelp}>
            Uz poruku u četu šalje i obaveštenje na telefon.
          </span>
          <Switch
            checked={form.push}
            onChange={(v) => set({ push: v })}
            disabled={!canManage}
          />
        </div>
      </div>
      <div className={styles.field}>
        <span>Dani slanja</span>
        <span className={styles.fieldHelp}>
          Poruke se šalju samo ovim danima.
        </span>
        <Checkbox.Group
          value={form.activeDays}
          onChange={(v) => set({ activeDays: v })}
          options={DAYS.map(([value, label]) => ({ value, label }))}
          disabled={!canManage}
        />
      </div>
      <div className={styles.field}>
        <span>Ton i obavezna pravila za svaku poruku</span>
        <span className={styles.fieldHelp}>
          Važi za svaku poruku, uz uputstvo iz pravila.
        </span>
        <Input.TextArea
          className={styles.textarea}
          variant="borderless"
          value={form.style}
          autoSize={{ minRows: 5, maxRows: 16 }}
          onChange={(e) => set({ style: e.target.value })}
          disabled={!canManage}
        />
      </div>
      {canManage && (
        <Button
          type="primary"
          className={styles.saveBtn}
          onClick={save}
          loading={saving}
          disabled={!changed}
        >
          Sačuvaj podešavanja
        </Button>
      )}
      {settings.version && (
        <p className={styles.dim}>
          Verzija {settings.version}, sačuvana{' '}
          {dayjs(settings.savedAt).format('DD.MM.YYYY. HH:mm')}.
        </p>
      )}
    </div>
  );
};

RuleCard.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string,
    status: PropTypes.string,
    origin: PropTypes.string,
    version: PropTypes.number,
    definition: PropTypes.object.isRequired,
    pseudocode: PropTypes.string,
    updatedAt: PropTypes.string,
    updatedBy: PropTypes.object,
    deletedAt: PropTypes.string,
  }).isRequired,
  catalog: PropTypes.shape({ kinds: PropTypes.array.isRequired }).isRequired,
  canManage: PropTypes.bool,
  onChanged: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
};

Settings.propTypes = {
  settings: PropTypes.shape({
    config: PropTypes.object.isRequired,
    version: PropTypes.number,
    savedAt: PropTypes.string,
  }).isRequired,
  canManage: PropTypes.bool,
  onSaved: PropTypes.func.isRequired,
};

const AiPravila = () => {
  const { can, loading: permLoading } = usePermissions();
  const canRead = can('ai:outreach:read');
  const canManage = can('ai:outreach:manage');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [tab, setTab] = useState('rules');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [newDraft, setNewDraft] = useState(null);
  const [historyRule, setHistoryRule] = useState(null);
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState(null);

  const load = useCallback(async () => {
    const res = await outreachRulesApi.list(showDeleted);
    if (!res.success) setError(res.message);
    else {
      setError(null);
      setData(res.data);
    }
  }, [showDeleted]);

  useEffect(() => {
    if (!permLoading && canRead) load();
  }, [permLoading, canRead, load]);

  useEffect(() => {
    if (tab !== 'history') return;
    outreachRulesApi
      .allHistory()
      .then((res) => setAllEvents(res.success ? res.data : []));
  }, [tab, data]);

  const openHistory = async (rule) => {
    setHistoryRule(rule);
    setEvents([]);
    const res = await outreachRulesApi.history(rule.id);
    setEvents(res.success ? res.data : []);
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    const res = await outreachRulesApi.generate(prompt.trim());
    setGenerating(false);
    if (!res.success) {
      toast.error(res.message);
      return;
    }
    setNewDraft(toDraft(res.data, prompt.trim(), data.catalog));
  };

  const rules = useMemo(() => data?.rules ?? [], [data]);

  if (permLoading) return <Spin />;
  if (!canRead) {
    return (
      <ContentLayout pt={true}>
        <Alert
          type="warning"
          showIcon
          message="Nemate dozvolu za pregled AI pravila."
        />
      </ContentLayout>
    );
  }

  const mode = MODE_TEXT[data?.settings?.config?.mode] || MODE_TEXT.off;

  return (
    <ConfigProvider theme={THEME}>
      <ContentLayout pt={true}>
        <div className={styles.page}>
          <div className={styles.pageHead}>
            <div>
              <p className="title">AI pravila</p>
              <p className={styles.dim}>
                Kada se AI asistent sam javlja korisniku. Opišite pravilo
                rečenicom, proverite šta je AI razumeo i sačuvajte. Sve izmene
                se pamte sa imenom onoga ko ih je napravio.
              </p>
            </div>
            {data && (
              <div className={styles.modeBox}>
                <Tag color={mode.color}>{mode.label}</Tag>
                <span className={styles.dim}>{mode.help}</span>
                <SchedulerStatus scheduler={data.scheduler} />
              </div>
            )}
          </div>

          {error && <Alert type="error" showIcon message={error} />}
          {!data && !error && <Spin />}

          {data && (
            <Tabs
              activeKey={tab}
              onChange={setTab}
              tabBarGutter={20}
              items={[
                {
                  key: 'rules',
                  label: `Pravila (${rules.filter((r) => !r.deletedAt).length})`,
                  children: (
                    <div className={styles.stack}>
                      {canManage && (
                        <section className={styles.newRule}>
                          <div>
                            <h3>Novo pravilo</h3>
                            <span className={styles.dim}>
                              Opišite rečima kome i kada AI treba da se javi,
                              ili napravite pravilo ručno.
                            </span>
                          </div>
                          {!newDraft ? (
                            <>
                              <Input.TextArea
                                autoSize={{ minRows: 2, maxRows: 5 }}
                                placeholder={`npr. „${EXAMPLES[0]}“`}
                                value={prompt}
                                maxLength={1000}
                                onChange={(e) => setPrompt(e.target.value)}
                              />
                              <div className={styles.examples}>
                                {EXAMPLES.map((ex) => (
                                  <button
                                    key={ex}
                                    type="button"
                                    className={styles.example}
                                    onClick={() => setPrompt(ex)}
                                  >
                                    {ex}
                                  </button>
                                ))}
                              </div>
                              <Space wrap>
                                <Button
                                  type="primary"
                                  onClick={generate}
                                  loading={generating}
                                  disabled={!prompt.trim()}
                                >
                                  Napravi pravilo
                                </Button>
                                <Button
                                  onClick={() =>
                                    setNewDraft({
                                      rule: emptyRule(data.catalog),
                                      check: null,
                                    })
                                  }
                                >
                                  Napravi ručno
                                </Button>
                              </Space>
                            </>
                          ) : (
                            <RuleDraft
                              canReview={canManage}
                              catalog={data.catalog}
                              draft={newDraft}
                              onSaved={() => {
                                setNewDraft(null);
                                setPrompt('');
                                load();
                              }}
                              onCancel={() => setNewDraft(null)}
                            />
                          )}
                        </section>
                      )}

                      <div className={styles.listHead}>
                        <div className={styles.listTitle}>
                          <h3>
                            Postojeća pravila (
                            {rules.filter((r) => !r.deletedAt).length})
                          </h3>
                          <span className={styles.dim}>
                            Ako više pravila važi za istog korisnika istog dana,
                            šalje se samo jedno: ono sa manjim prioritetom.
                          </span>
                        </div>
                        <Checkbox
                          checked={showDeleted}
                          onChange={(e) => setShowDeleted(e.target.checked)}
                        >
                          prikaži obrisana
                        </Checkbox>
                      </div>

                      {rules.map((r) => (
                        <RuleCard
                          key={`${r.id}-${r.version}`}
                          rule={r}
                          catalog={data.catalog}
                          canManage={canManage}
                          onChanged={load}
                          onHistory={openHistory}
                        />
                      ))}
                    </div>
                  ),
                },
                {
                  key: 'history',
                  label: 'Istorija izmena',
                  children: allEvents ? (
                    <RuleHistory events={allEvents} showRuleKey />
                  ) : (
                    <Spin />
                  ),
                },
                {
                  key: 'settings',
                  label: 'Opšta podešavanja',
                  children: (
                    <Settings
                      settings={data.settings}
                      canManage={canManage}
                      onSaved={load}
                    />
                  ),
                },
              ]}
            />
          )}

          <Drawer
            open={!!historyRule}
            onClose={() => setHistoryRule(null)}
            width={Math.min(640, window.innerWidth)}
            title={
              historyRule ? `Istorija: ${historyRule.definition.name}` : ''
            }
          >
            <RuleHistory events={events} />
          </Drawer>
        </div>
      </ContentLayout>
    </ConfigProvider>
  );
};

export default AiPravila;
